import {
	Alert,
	Button,
	Checkbox,
	Col,
	Divider,
	Form,
	Input,
	InputNumber,
	Modal,
	Row,
	Space,
	Tooltip,
	Typography,
} from 'antd';
import { ScrapBreakdown } from '../ScrapInfo/ScrapInfoButton';
import ScrapRecommendation from '../ScrapInfo/ScrapRecommendation';
import BuildingTag from '../BuildingTag';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import { useIdempotencyKey } from '../../utils/idempotency';
import { useEffect, useRef, useState } from 'react';
import { useGetOrderLineItemByIdQuery } from '../../redux/api/order';
import {
	Material,
	MaterialLength,
	MaterialReservation,
} from '../../interfaces/MaterialInventory.interface';
import {
	useAddMaterialReservationMutation,
	useCalculateScrapQuantityMutation,
	useGetMaterialByIdQuery,
	useGetMaterialLengthByIdQuery,
	useReserveMaterialMutation,
	useUpdateMaterialReservationMutation,
} from '../../redux/api/material';
import {
	DEFAULT_SAW_KERF,
	DEFAULT_TRIM_CUT_LENGTH,
} from '../../utils';
import SalesOrderLineItemSelect from '../SalesOrderLineItemSelect';
import { Lineitem, ProductionMethod } from '../../interfaces/Ordel.Interface';
import {
	maximumBaseCutQuantity,
	reservableBarLengths,
} from './maxCutQuantity';

const { Title, Text } = Typography;

// Mirrors the backend `reserve_material` availability check: a live sum of each
// bar's available length, restricted to the building the reservation targets.
// The Material's stored `total_available_length` is a global, denormalized value
// that does not respect the building filter, so it cannot be shown here.
function getBuildingScopedAvailableLength(
	selectedMaterial: Material | null,
	buildingId?: string | null
) {
	const lengths = (selectedMaterial as any)?.lengths;
	if (!Array.isArray(lengths)) {
		// Fall back to the stored value if per-bar data isn't loaded yet.
		return Number(selectedMaterial?.total_available_length ?? 0);
	}

	// Same bars the packed maximum uses. Unlocated, hold, and scrapped
	// lengths cannot be reserved, so they must not inflate this label.
	const bars = reservableBarLengths(lengths, buildingId) ?? [];
	const total = bars.reduce((sum, length) => sum + length, 0);
	return Number(total.toFixed(2));
}

function calculateReservableLength(
	selectedMaterial: Material | null,
	selectedMaterialLength: MaterialLength | null,
	selectedMaterialReservation: MaterialReservation | null,
	reserveMultipleLengths: boolean,
	buildingId?: string | null
) {
	if (selectedMaterialLength && selectedMaterialReservation) {
		return Number(
			(
				Number(selectedMaterialLength.total_available_length) +
				Number(selectedMaterialReservation.reserved_length)
			).toFixed(2)
		);
	}

	if (selectedMaterial && reserveMultipleLengths) {
		return getBuildingScopedAvailableLength(selectedMaterial, buildingId);
	}

	if (selectedMaterialLength && !reserveMultipleLengths) {
		return Number(selectedMaterialLength.total_available_length);
	}

	return 0;
}

interface Props {
	lineItem?: Lineitem;
	material_cut_length?: string | number | null;
	saw_kerf?: number;
	material_cut_quantity?: number;
	required_trim_cut_length?: number;
	selectedMaterialReservation: MaterialReservation | null;
	isMaterialReservationModalOpen: boolean;
	selectedMaterial: Material;
	selectedMaterialLength: MaterialLength | null;
	reserveMultipleLengths: boolean;
	lockedBuildingId?: string | null;
	onClose: () => void;
	refetchData: () => void;
}

const MaterialReservationModal = (props: Props) => {
	const [selectedOrderLineItem, setSelectedOrderLineItem] = useState<
		string | null
	>(null);

	const [materialId, setMaterialId] = useState<string | null>(null);
	const [materialLengthId, setMaterialLengthId] = useState<string | null>();

	const [reserveForOrderLineItem, setReserveForOrderLineItem] =
		useState<boolean>(
			!props?.selectedMaterialReservation
				? true
				: props.selectedMaterialReservation?.order_line_item
					? true
					: false
		);

	const orderLineItemQuery = useGetOrderLineItemByIdQuery(
		{ id: selectedOrderLineItem },
		{ skip: !selectedOrderLineItem, refetchOnMountOrArgChange: true }
	);

	const materialQuery = useGetMaterialByIdQuery(
		{ id: materialId },
		{ skip: !materialId, refetchOnMountOrArgChange: true }
	);

	const materialLengthQuery = useGetMaterialLengthByIdQuery(
		{ id: materialLengthId },
		{ skip: !materialLengthId, refetchOnMountOrArgChange: true }
	);

	const [addMaterialReservation, addMaterialReservationResult] = useAddMaterialReservationMutation();
	const [reserveMaterial, reserveMaterialResult] = useReserveMaterialMutation();
	// Stable key across retries so a lost response doesn't double-reserve.
	const { getKey, rotate } = useIdempotencyKey();
	const [updateMaterialReservation, updateMaterialReservationResult] =
		useUpdateMaterialReservationMutation();
	const [calculateScrapQuantity] = useCalculateScrapQuantityMutation();
	// Rapid edits to cut length / quantity fire overlapping scrap lookups that
	// can resolve out of order; only the newest one may write the form.
	const latestScrapRequest = useRef(0);

	// Drive the OK button spinner and lock the modal actions while any of the
	// reserve/add/update requests is in flight, so the operator can't double-submit.
	const isSubmitting =
		reserveMaterialResult.isLoading ||
		addMaterialReservationResult.isLoading ||
		updateMaterialReservationResult.isLoading;

	const [form] = Form.useForm();

	const isSwissProduction =
		props.lineItem?.partsbadger_production_method === ProductionMethod.Swiss;

	// Multi-bar reservations must not calculate from the summed length while
	// each bar is still loading. That sum is the over-count PTB-6180 fixes.
	const shouldPackAcrossBars =
		!isSwissProduction && Boolean(props.reserveMultipleLengths);
	const barsLoaded = Array.isArray((materialQuery?.data as any)?.lengths);
	const waitingForBars = shouldPackAcrossBars && !barsLoaded;

	// Standard production only: the scrap quantity is looked up from
	// the volume/cost table using the concrete material selected here, then added
	// to the base cut quantity so those extra pieces are physically cut and
	// reserved. Swiss keeps its own formula and folds scrap into
	// material_cut_quantity upstream.
	const [estimatedScrapQuantity, setEstimatedScrapQuantity] =
		useState<number>(0);

	// Breakdown of how the scrap value was derived, shown in the info popover so
	// the user can see the order quantity, the estimated cost per piece, and the
	// table tier (range) the value fell into.
	const [scrapBreakdown, setScrapBreakdown] =
		useState<ScrapBreakdown | null>(null);

	const materialBuilding =
		props.selectedMaterialLength?.inventory_location?.shelf?.rack?.building ??
		(materialLengthQuery?.data as any)?.inventory_location?.shelf?.rack?.building ??
		(props.selectedMaterial?.building_id && props.selectedMaterial?.building_name
			? {
					id: props.selectedMaterial.building_id,
					name: props.selectedMaterial.building_name,
			  }
			: null);

	function calculateRequiredMaterialLength(
		materialCutLength: number,
		sawKerf: number,
		materialCutQuantity: number,
		trimCutLength?: number,
		scrapQuantity?: number
	) {
		const trim = isSwissProduction ? 0 : (trimCutLength ?? form.getFieldValue('required_trim_cut_length') ?? DEFAULT_TRIM_CUT_LENGTH);
		// Standard: reserve room for base + scrap physical cuts. Swiss already
		// folds its scrap into materialCutQuantity upstream.
		const scrap = isSwissProduction
			? 0
			: (scrapQuantity ?? estimatedScrapQuantity ?? 0);
		let total: number;

		if (isSwissProduction) {
			// Swiss formula: (part length + cut off length) * (parts + estimated scrap qty) * 12 / 11
			total = Number(
				Number((materialCutLength + sawKerf) * materialCutQuantity * (12 / 11)).toFixed(2)
			);
		} else {
			// Standard formula: ((materialCutLength + sawKerf) * (materialCutQuantity + scrap)) + trimCutLength
			total = Number(
				Number((materialCutLength + sawKerf) * (materialCutQuantity + scrap) + trim).toFixed(2)
			);
		}

		form.setFieldValue('reserved_length', total);

		return total ?? 1;
	}

	// The scrap lookup grid is keyed on the ORDER quantity (the line item's
	// "Cut Quantity"), not the editable "Material Cut Quantity" field of this
	// reservation. Reserving or cutting fewer/more pieces from a given bar must
	// not change how many extra scrap pieces the job needs.
	function getScrapQuantityBasis(): number {
		const orderQuantity =
			Number(orderLineItemQuery?.data?.quantity) ||
			Number(props.material_cut_quantity) ||
			0;
		if (orderQuantity >= 1) {
			return orderQuantity;
		}
		return Number(form.getFieldValue('material_cut_quantity')) || 0;
	}

	// Scrap already reserved on the OTHER reservations for this order line item
	// (PTB-5965). The scrap allowance is an order-level, nonlinear value keyed on
	// the order quantity, so it must be allocated once for the whole order rather
	// than recomputed per bar. When editing, this line item's own stored scrap is
	// excluded so we don't count it against the remaining allowance twice.
	function getScrapAlreadyAllocatedByOthers(): number {
		const total =
			Number(
				(orderLineItemQuery?.data as any)?.total_reserved_scrap_quantity
			) || 0;
		const ownScrap = props.selectedMaterialReservation
			? Number(
					(props.selectedMaterialReservation as any)
						.estimated_scrap_quantity
			  ) || 0
			: 0;
		return Math.max(0, total - ownScrap);
	}

	// Standard production only: look up the scrap quantity for the selected
	// material and refresh the reserved length. The field stays editable.
	// `quantity` is the order Cut Quantity used for the lookup grid, while
	// `materialCutQuantity` is the actual pieces to cut, used only to size the
	// reserved length.
	//
	// The table lookup yields the scrap allowance for the WHOLE order. That
	// allowance can legitimately be 0 (expensive / low-volume jobs), and it is
	// allocated across reservations: the first reservation for the line item
	// takes the full allowance and later ones only get what is left, so the
	// total scrap across all reservations never exceeds the table value.
	async function recomputeScrap(
		quantity: number,
		materialCutLength?: number | string | null,
		materialCutQuantity?: number | string | null
	): Promise<number> {
		if (isSwissProduction) {
			return 0;
		}

		const requestId = ++latestScrapRequest.current;
		const material = props.selectedMaterial ?? (materialQuery?.data as Material);
		// Smart fallback used only when the table lookup can't run (unknown cost).
		const fallback = Math.max(Math.round((quantity || 0) * 0.15), 1);
		// Full order-level allowance from the table (or fallback).
		let allowance = fallback;
		let breakdown: ScrapBreakdown = {
			quantity,
			cost_per_piece: null,
			estimated_scrap_quantity: fallback,
			used_fallback: true,
			fallback_reason: 'cost_per_piece_unavailable',
			fallback_percent: 0.15,
			quantity_tier: null,
			cost_tier: null,
			manual: false,
		};

		if (quantity && quantity >= 1) {
			try {
				const res = await calculateScrapQuantity({
					quantity,
					material_id: material?.id ?? null,
					material_cut_length: materialCutLength
						? Number(materialCutLength)
						: null,
					height: material?.height,
					width: material?.width,
					outer_diameter: material?.outer_diameter,
					inner_diameter: material?.inner_diameter,
					wall_thickness: material?.wall_thickness,
					// Only send an actual positive cost. A 0/blank cost means "no
					// purchased cost", so the backend should fall back to the
					// material type's default MaterialCost instead of $0.
					cost_per_cubic_inch:
						Number(material?.cost_per_cubic_inch) > 0
							? material?.cost_per_cubic_inch
							: null,
					material_type_id: material?.type?.id ?? null,
				}).unwrap();
				allowance = Math.max(
					Number(res?.estimated_scrap_quantity ?? fallback) || 0,
					0
				);
				breakdown = { ...(res as ScrapBreakdown), manual: false };
			} catch {
				allowance = fallback;
			}
		}

		// Allocate the order-level allowance across reservations. Only reservations
		// tied to an order line item share the allowance; free-form reservations
		// keep the raw allowance.
		const alreadyAllocated = reserveForOrderLineItem
			? getScrapAlreadyAllocatedByOthers()
			: 0;
		const scrap = Math.max(allowance - alreadyAllocated, 0);

		if (requestId !== latestScrapRequest.current) {
			// A newer lookup is already in flight for different inputs; writing
			// this result would show scrap for a quantity the operator changed.
			return scrap;
		}

		setScrapBreakdown({
			...breakdown,
			order_scrap_allowance: allowance,
			already_allocated_scrap: alreadyAllocated,
			estimated_scrap_quantity: scrap,
		});
		setEstimatedScrapQuantity(scrap);
		form.setFieldValue('estimated_scrap_quantity', scrap);

		const cutLen =
			materialCutLength ?? form.getFieldValue('material_cut_length');
		const sawKerf = form.getFieldValue('saw_kerf');
		const cutQtyForLength =
			materialCutQuantity ?? form.getFieldValue('material_cut_quantity');
		if (cutLen && cutQtyForLength) {
			calculateRequiredMaterialLength(
				Number(cutLen),
				Number(sawKerf),
				Number(cutQtyForLength),
				undefined,
				scrap
			);
		}

		return scrap;
	}

	// The scrap field is editable: the user is responsible for the final value.
	// This updates the value and the reserved length, but leaves scrapBreakdown
	// (the table recommendation shown beside the field) untouched.
	function handleChangeScrapQuantity(value: number | string | null) {
		const scrap = Number(value) || 0;
		setEstimatedScrapQuantity(scrap);
		form.setFieldValue('estimated_scrap_quantity', scrap);
		const materialCutLength = form.getFieldValue('material_cut_length');
		const sawKerf = form.getFieldValue('saw_kerf');
		const materialCutQuantity = form.getFieldValue('material_cut_quantity');
		if (materialCutLength && materialCutQuantity) {
			calculateRequiredMaterialLength(
				Number(materialCutLength),
				Number(sawKerf),
				Number(materialCutQuantity),
				undefined,
				scrap
			);
		}
	}

	function handleChangeSelectedLineItem(value: string) {
		setSelectedOrderLineItem(value);
	}

	function handleChangeMaterialCutLength(value: number | string) {
		if (value) {
			const materialCutQuantity = form.getFieldValue('material_cut_quantity');
			const sawKerf = form.getFieldValue('saw_kerf');
			calculateRequiredMaterialLength(
				Number(value),
				sawKerf,
				materialCutQuantity
			);
			if (!isSwissProduction && materialCutQuantity) {
				recomputeScrap(
					getScrapQuantityBasis(),
					value,
					materialCutQuantity
				);
			}
		}
	}

	function handleChangeMaterialCutQuantity(
		value: number | string | undefined | null
	) {
		if (value) {
			const materialCutLength = form.getFieldValue('material_cut_length');
			const sawKerf = form.getFieldValue('saw_kerf');

			calculateRequiredMaterialLength(
				Number(materialCutLength),
				sawKerf,
				Number(value)
			);
			if (!isSwissProduction) {
				recomputeScrap(
					getScrapQuantityBasis(),
					materialCutLength,
					Number(value)
				);
			}
		}
	}

	useEffect(() => {
		if (props.lineItem) {
			setSelectedOrderLineItem(props.lineItem.id);
			form.setFieldValue('order_line_item', props.lineItem.id);
		}
	}, [props.lineItem]);

	useEffect(() => {
		if (props.selectedMaterial) {
			setMaterialId(props.selectedMaterial.id);
		} else {
			setMaterialId(null);
		}
	}, [props.selectedMaterial]);

	useEffect(() => {
		if (props.selectedMaterialLength) {
			setMaterialLengthId(props.selectedMaterialLength.id);
		} else {
			setMaterialLengthId(null);
		}
	}, [props.selectedMaterialLength]);

	useEffect(() => {
		if (props.selectedMaterialReservation?.material_length?.material) {
			setMaterialId(props.selectedMaterialReservation.material_length.material);
			setMaterialLengthId(props.selectedMaterialReservation.material_length.id);
		}
		if (props.selectedMaterialReservation) {
			setEstimatedScrapQuantity(
				Number(
					(props.selectedMaterialReservation as any)
						.estimated_scrap_quantity ?? 0
				)
			);
			// The reservation tables open this modal without `lineItem`, so
			// without this the line item was never fetched while editing: the
			// order-level scrap allowance had nothing to net against (PTB-5965)
			// and the allocation guard would compare against a total of 0.
			const editedOrderLineItemId =
				props.selectedMaterialReservation.order_line_item?.id;
			if (editedOrderLineItemId) {
				setSelectedOrderLineItem(editedOrderLineItemId);
			}
		}
	}, [props.selectedMaterialReservation]);

	useEffect(() => {
		if (orderLineItemQuery.isSuccess) {
			// When the modal is opened from Material Inventory the reserve props
			// (saw_kerf / material_cut_length / material_cut_quantity) are not
			// provided. Attaching an order line item here must NOT clobber the
			// values the operator already typed: overwriting material_cut_length
			// with undefined wipes it, so the scrap lookup can no longer derive a
			// cost per piece and silently drops to the smart fallback even for a
			// costed material. Preserve the current form values first, then fall
			// back to sensible defaults.
			form.setFieldValue(
				'saw_kerf',
				props.saw_kerf ?? form.getFieldValue('saw_kerf') ?? 0.07
			);
			form.setFieldValue(
				'material_cut_length',
				props.material_cut_length ??
					form.getFieldValue('material_cut_length')
			);
			form.setFieldValue(
				'material_cut_quantity',
				props.material_cut_quantity ??
					form.getFieldValue('material_cut_quantity') ??
					orderLineItemQuery?.data?.quantity
			);

			calculateRequiredMaterialLength(
				Number(form.getFieldValue('material_cut_length')),
				form.getFieldValue('saw_kerf'),
				form.getFieldValue('material_cut_quantity')
			);

			if (!isSwissProduction && !props.selectedMaterialReservation) {
				recomputeScrap(
					getScrapQuantityBasis(),
					form.getFieldValue('material_cut_length'),
					form.getFieldValue('material_cut_quantity')
				);
			}
		}
	}, [orderLineItemQuery]);

	return (
		<Modal
			title={
				<Space>
					<span>Reserve Material</span>
					<BuildingTag name={materialBuilding?.name} showIcon={false} />
				</Space>
			}
			open={props.isMaterialReservationModalOpen}
			centered
			width={'760px'}
			onCancel={() => {
				if (isSubmitting) {
					return;
				}
				props.onClose();
				form.resetFields();
				setReserveForOrderLineItem(true);
			}}
			destroyOnClose
			maskClosable={!isSubmitting}
			keyboard={!isSubmitting}
			confirmLoading={isSubmitting}
			cancelButtonProps={{ disabled: isSubmitting }}
			okButtonProps={{
				loading: isSubmitting,
				disabled: isSubmitting,
				onClick: async () => {
					let values;
					try {
						values = await form.validateFields();
					} catch {
						return;
					}
					values.material_cut_length = parseFloat(
						parseFloat(values.material_cut_length).toFixed(2)
					);
					// The form's "Material Cut Quantity" holds the base order
					// quantity. material_cut_quantity is stored as the total pieces
					// to physically cut (base + scrap) so the saw cut flow directs
					// the operator to cut the extra scrap pieces. estimated_scrap_quantity
					// is persisted separately as the informational breakdown.
					const baseQty = Number(values.material_cut_quantity) || 0;
					// PTB-5965: scrap can be 0 (expensive/low-volume jobs, or when
					// the order's allowance was already reserved on an earlier
					// reservation). Only guard against negative values.
					const scrapQty = isSwissProduction
						? 0
						: Math.max(Number(values.estimated_scrap_quantity) || 0, 0);
					values.material_cut_quantity = baseQty + scrapQty;
					values.estimated_scrap_quantity = scrapQty;
					// PTB-5965: the scrap above was netted against the scrap this
					// modal saw already allocated on the order line item. Send
					// that total on every write path so the server rejects the
					// request if the allowance moved meanwhile instead of
					// allocating it twice.
					const scrapAllocationGuard =
						reserveForOrderLineItem && !isSwissProduction
							? {
									expected_reserved_scrap_quantity:
										Number(
											(orderLineItemQuery?.data as any)
												?.total_reserved_scrap_quantity
										) || 0,
								}
							: {};
					if (props.selectedMaterialReservation) {
						// An edit that leaves scrap untouched (cut length, quantity,
						// status) can't double-allocate, so it is not guarded.
						const storedScrapQty =
							Number(
								(props.selectedMaterialReservation as any)
									.estimated_scrap_quantity
							) || 0;
						await updateMaterialReservation({
							id: props.selectedMaterialReservation.id,
							values: {
								...values,
								...(scrapQty !== storedScrapQty
									? scrapAllocationGuard
									: {}),
							},
							orderLineItemId: selectedOrderLineItem,
						})
							.unwrap()
							.then(() => {
								props.refetchData();
								form.resetFields();
								props.onClose();
							});
					} else {
					if (props.reserveMultipleLengths) {
						if (materialQuery?.data) {
							await reserveMaterial({
								id: materialQuery?.data.id,
								values: {
									...values,
									...scrapAllocationGuard,
									...(props.lockedBuildingId ? { building_id: props.lockedBuildingId } : {}),
								},
								idempotencyKey: getKey(),
							})
								.unwrap()
								.then(() => {
									rotate();
									props.refetchData();
									form.resetFields();
									props.onClose();
								});
						}
						} else {
							if (materialLengthQuery?.data) {
								await addMaterialReservation({
									values: {
										...values,
										...scrapAllocationGuard,
										material_length: materialLengthQuery?.data?.id,
									},
									idempotencyKey: getKey(),
								})
									.unwrap()
									.then(() => {
										rotate();
										props.refetchData();
										form.resetFields();
										props.onClose();
									});
							}
						}
					}
				},
			}}
		>
		{reserveMaterialResult.isError ? (
			<div className="mb-3">
				<Alert
					type="error"
					className="mb-3"
					message={<Title level={5}>An Error Occurred</Title>}
					description={getApiErrorMessage(reserveMaterialResult.error)}
				/>
			</div>
		) : null}
		{addMaterialReservationResult.isError ? (
			<div className="mb-3">
				<Alert
					type="error"
					className="mb-3"
					message={<Title level={5}>An Error Occurred</Title>}
					description={getApiErrorMessage(
						addMaterialReservationResult.error,
						'An unexpected error occurred while creating the reservation.'
					)}
				/>
			</div>
		) : null}
		{updateMaterialReservationResult.isError ? (
			<div className="mb-3">
				<Alert
					type="error"
					className="mb-3"
					message={<Title level={5}>An Error Occurred</Title>}
					description={getApiErrorMessage(
						updateMaterialReservationResult.error,
						'An unexpected error occurred while updating the reservation.'
					)}
				/>
			</div>
		) : null}

			<Form
				layout="vertical"
				requiredMark={false}
				form={form}
			initialValues={
				props.selectedMaterialReservation
					? {
						order_line_item:
							props.selectedMaterialReservation.order_line_item?.id ?? null,
						material_cut_length: Number(
							props.selectedMaterialReservation.material_cut_length
						),
						// Stored material_cut_quantity is the total pieces to cut
						// (base + scrap). The field edits the base order quantity, so
						// reconstruct it by subtracting the stored scrap breakdown.
						material_cut_quantity: Math.max(
							0,
							Number(
								props.selectedMaterialReservation.material_cut_quantity
							) -
								Number(
									(props.selectedMaterialReservation as any)
										.estimated_scrap_quantity ?? 0
								)
						),
						saw_kerf: Number(props.selectedMaterialReservation.saw_kerf),
						required_trim_cut_length: Number(
							props.selectedMaterialReservation.required_trim_cut_length ?? DEFAULT_TRIM_CUT_LENGTH
						),
						estimated_scrap_quantity: Number(
							(props.selectedMaterialReservation as any)
								.estimated_scrap_quantity ?? 0
						),
						reserved_length: Number(
							props.selectedMaterialReservation.reserved_length
						),
						reserved_for_order_line_item: props.selectedMaterialReservation
							.order_line_item
							? true
							: false,
						reserved_for_description:
							props.selectedMaterialReservation.reserved_for_description,
					}
				: {
					saw_kerf: props?.saw_kerf ?? DEFAULT_SAW_KERF,
					required_trim_cut_length: props?.required_trim_cut_length ?? DEFAULT_TRIM_CUT_LENGTH,
					estimated_scrap_quantity: 0,
					order_line_item: props?.lineItem?.id ?? null,
				}
			}
			>
				<>
					<Form.Item
						name={'reserved_for_order_line_item'}
						hidden={Boolean(props.lineItem)}
					>
						<Checkbox
							checked={reserveForOrderLineItem}
							onChange={(e) => setReserveForOrderLineItem(e.target.checked)}
						>
							Reserve for an Order Line Item
						</Checkbox>
					</Form.Item>

					{reserveForOrderLineItem ? (
						<>
							<Form.Item
								label="Order Line Item"
								name={'order_line_item'}
								rules={[
									{
										required: true,
									},
								]}
								hidden={Boolean(props.lineItem)}
							>
								<SalesOrderLineItemSelect
									showThumbnail
									materialBuildingId={materialBuilding?.id ?? null}
									value={selectedOrderLineItem}
									onChange={(value) => {
										handleChangeSelectedLineItem(value);
									}}
								/>
							</Form.Item>

							{orderLineItemQuery.isSuccess ? (
								<>
									<Row gutter={[16, 16]}>
										<Col span={4}>
											<Text strong>Order</Text>
											<br />
											<Text>{orderLineItemQuery?.data?.order.name}</Text>
										</Col>
										<Col span={10}>
											<Text strong>Part</Text>
											<br />
											<Text>{orderLineItemQuery?.data?.name}</Text>
										</Col>
										<Col span={6}>
											<Text strong>Material</Text>
											<br />
											<Text>{orderLineItemQuery?.data?.material_name}</Text>
										</Col>
										<Col span={4}>
											<Text strong>Quantity</Text>
											<br />
											<Text>{orderLineItemQuery?.data?.quantity}</Text>
										</Col>
									</Row>
									<Divider />
								</>
							) : null}
						</>
					) : (
						<Form.Item
							label="Reserving For"
							name={'reserved_for_description'}
							rules={[
								{
									required: true,
								},
							]}
							hidden={Boolean(props.lineItem)}
						>
							<Input placeholder="Describe what your are reserving this material for" />
						</Form.Item>
					)}
				</>

				<Row gutter={[8, 8]}>
					<Col span={8}>
						<Form.Item
							label={isSwissProduction ? 'Part Length' : 'Material Cut Length'}
							name="material_cut_length"
							rules={[
								{
									required: true,
								},
							]}
						>
							<InputNumber
								onChange={(value) => {
									if (value) {
										handleChangeMaterialCutLength(value);
									}
								}}
								step={0.1}
								precision={2}
								style={{ width: '100%', marginBottom: 0 }}
								suffix={'inches'}
								min={0.4}
							/>
						</Form.Item>
					</Col>
				<Col span={8}>
					<Form.Item
						label={isSwissProduction ? 'Cut Off Length' : 'Saw Kerf'}
						name="saw_kerf"
						rules={[
							{
								required: true,
							},
						]}
					>
						<InputNumber
							onChange={(value) => {
								console.log(value);
								const sawKerf = Number(value);
								if (sawKerf >= 0) {
									const materialCutQuantity = form.getFieldValue(
										'material_cut_quantity'
									);
									const materialCutLength = form.getFieldValue(
										'material_cut_length'
									);
									calculateRequiredMaterialLength(
										Number(materialCutLength),
										Number(value),
										materialCutQuantity
									);
								}
							}}
							style={{ width: '100%', marginBottom: 0 }}
							suffix={'inches'}
						/>
					</Form.Item>
				</Col>
				{!isSwissProduction && (
					<Col span={8}>
						<Form.Item
							label="Required Trim Cut Length"
							name="required_trim_cut_length"
						>
							<InputNumber
								onChange={(value) => {
									const materialCutLength = form.getFieldValue('material_cut_length');
									const sawKerf = form.getFieldValue('saw_kerf');
									const materialCutQuantity = form.getFieldValue('material_cut_quantity');
									calculateRequiredMaterialLength(
										Number(materialCutLength),
										Number(sawKerf),
										Number(materialCutQuantity),
										Number(value)
									);
								}}
								step={0.1}
								precision={3}
								min={0}
								style={{ width: '100%', marginBottom: 0 }}
								suffix={'inches'}
							/>
						</Form.Item>
					</Col>
				)}
					<Col span={8}>
						<Form.Item
							label={isSwissProduction ? 'Total Parts' : 'Material Cut Quantity'}
							name="material_cut_quantity"
							dependencies={[
								'material_cut_length',
								'saw_kerf',
								'required_trim_cut_length',
								'estimated_scrap_quantity',
							]}
							rules={[
								{
									required: true,
								},
								{
									validator: (_, value) => {
										// Multi-bar only. Scrap pieces are physical cuts, so a base
										// of 0 with scrap still has to fit on the bars after trim.
										// Single-bar and Swiss submits are unchanged.
										if (
											!shouldPackAcrossBars ||
											value === undefined ||
											value === null ||
											value === ''
										) {
											return Promise.resolve();
										}
										const scrap =
											Number(
												form.getFieldValue('estimated_scrap_quantity')
											) || 0;
										const requestedCuts = Number(value) + scrap;
										// Still reject an empty request while bars are loading.
										// Submitting 0 cuts would mark material selection complete.
										if (requestedCuts < 1) {
											return Promise.reject(
												new Error(
													'Requested cuts do not fit on the available bars.'
												)
											);
										}
										const rawLengths = (materialQuery?.data as any)
											?.lengths;
										if (!Array.isArray(rawLengths)) {
											return Promise.resolve();
										}
										const cutUnit =
											Number(form.getFieldValue('material_cut_length')) +
											Number(form.getFieldValue('saw_kerf'));
										const trim = Number(
											form.getFieldValue(
												'required_trim_cut_length'
											) ?? DEFAULT_TRIM_CUT_LENGTH
										);
										const capacity = maximumBaseCutQuantity({
											barLengths: reservableBarLengths(
												rawLengths,
												props.lockedBuildingId
											),
											totalAvailable: 0,
											cutUnit,
											trim,
											scrap: 0,
										});
										if (requestedCuts > capacity) {
											return Promise.reject(
												new Error(
													'Requested cuts do not fit on the available bars.'
												)
											);
										}
										return Promise.resolve();
									},
								},
							]}
						>
							<InputNumber
								onChange={handleChangeMaterialCutQuantity}
								style={{ width: '100%', marginBottom: 0 }}
							/>
						</Form.Item>
					</Col>
					{!isSwissProduction && (
						<Col span={8}>
						<Form.Item
							label={
								<Tooltip title="Extra pieces to cut in addition to the order quantity. Pre-filled with the recommended value from the volume/cost table (which can be 0); you can override it. Added to the cut quantity so the operator cuts these extra pieces.">
									Scrap Quantity
								</Tooltip>
							}
							name="estimated_scrap_quantity"
							rules={[{ required: true }]}
							style={{ marginBottom: 0 }}
						>
							<InputNumber
								min={0}
								step={1}
								onChange={handleChangeScrapQuantity}
								style={{ width: '100%', marginBottom: 0 }}
							/>
						</Form.Item>
							<ScrapRecommendation
								breakdown={scrapBreakdown}
								onApply={handleChangeScrapQuantity}
							/>
						</Col>
					)}
					<Col span={24}>
						{waitingForBars ? (
							<Text type="secondary" className="mb-2" style={{ display: 'block' }}>
								Material bars are still loading. The maximum cut quantity
								cannot be calculated until each bar is available.
							</Text>
						) : null}
						<Tooltip
							title={
								waitingForBars
									? 'Material bars are still loading. Wait until each bar is available before calculating the maximum.'
									: isSwissProduction
										? 'Calculate the maximum number of parts that can be produced from the selected material based on the Part Length and Cut Off Length.'
										: 'Calculate the maximum number of pieces that can be cut from the selected material based on the material cut length'
							}
						>
							{/* Disabled buttons do not receive hover events, so the tooltip sits on the span. */}
							<span style={{ display: 'block' }}>
							<Button
								block
								className="mb-3"
								disabled={waitingForBars}
								onClick={() => {
									if (waitingForBars) {
										return;
									}
									if (form.getFieldValue('material_cut_length')) {
										// Use the same available length that "Length to Reserve"
										// validates against so the suggested quantity can never
										// exceed it. For a per-bar reservation this is the selected
										// bar's available length (not the material-wide total),
										// which otherwise over-reserves against a single bar.
										const availableForCuts = calculateReservableLength(
											materialQuery?.data,
											materialLengthQuery?.data,
											props.selectedMaterialReservation,
											props.reserveMultipleLengths,
											props.lockedBuildingId
										);
										// Trim is added once to the reserved length (non-Swiss),
										// so it must be reserved out of the available length
										// before dividing by the per-cut size. Otherwise the
										// suggested quantity overflows the available length.
										const trim = isSwissProduction
											? 0
											: Number(
												form.getFieldValue('required_trim_cut_length') ??
												DEFAULT_TRIM_CUT_LENGTH
											);
										// The scrap pieces are cut from this same material, so the
										// reserved length is sized for (quantity + scrap) cuts. The
										// suggested quantity must leave room for them or it overflows
										// the available length by exactly those pieces (reported
										// 2026-08-27: 46 suggested on a 288 in. bar reserved 289.99 in.).
										// Read the same value calculateRequiredMaterialLength will use.
										const scrap = isSwissProduction ? 0 : (estimatedScrapQuantity ?? 0);
										const cutUnit =
											Number(form.getFieldValue('material_cut_length')) +
											Number(form.getFieldValue('saw_kerf'));
										// Non-Swiss reservations across several bars only fit whole
										// cuts on each bar (PTB-6180). Swiss and a single selected
										// bar still divide the summed length. Do not fall back to
										// that sum while the bars are loading: the button is
										// disabled, and this return covers a click that lands first.
										const rawLengths = (materialQuery?.data as any)?.lengths;
										if (shouldPackAcrossBars && !Array.isArray(rawLengths)) {
											return;
										}
										const barLengths = shouldPackAcrossBars
											? reservableBarLengths(
												rawLengths,
												props.lockedBuildingId
											)
											: null;
										const max = maximumBaseCutQuantity({
											barLengths,
											totalAvailable: availableForCuts,
											cutUnit,
											trim,
											scrap,
										});

										form.setFieldValue('material_cut_quantity', max);
										// setFieldValue does not re-run rules, so a previous
										// "does not fit" error would stay under this valid max.
										form
											.validateFields(['material_cut_quantity'])
											.catch(() => undefined);
										// Recalculate the length directly instead of going through
										// handleChangeMaterialCutQuantity. That helper re-runs the scrap
										// lookup keyed on the quantity this button just wrote, which is
										// wrong twice over: getScrapQuantityBasis documents that cutting
										// fewer/more pieces from a bar must not change the job's scrap
										// allowance, and on the free-form path (no order line item) that
										// basis IS this field, so the lookup feeds back into the value we
										// just solved for and overflows the bar again. It also short-
										// circuits on a falsy quantity, leaving the previous, larger length
										// on screen next to a max of 0.
										calculateRequiredMaterialLength(
											Number(form.getFieldValue('material_cut_length')),
											Number(form.getFieldValue('saw_kerf')),
											max,
											undefined,
											scrap
										);
									}
								}}
							>
								{isSwissProduction
									? 'Calculate the Maximum Number of Parts'
									: 'Calculate Maximum Cut Quantity'}
							</Button>
							</span>
						</Tooltip>
					</Col>
					{/* <Col span={24}>
							<Button
								block
								onClick={() => {
									form.setFieldValue(
										'reserved_length',
										calculateRequiredMaterialLength()
									);
								}}
								disabled={materialCutLength === 0}
							>
								Calculate Required Length
							</Button>
						</Col> */}
					{!props.reserveMultipleLengths ? (
						<Col span={24}>
							<Form.Item>
								<Checkbox
									onChange={(e) => {
										if (e.target.checked) {
											const maxAvailable = props.reserveMultipleLengths
												? getBuildingScopedAvailableLength(
													materialQuery?.data,
													props.lockedBuildingId
												)
												: materialLengthQuery?.data?.total_available_length;
											form.setFieldValue('reserved_length', maxAvailable);
										} else {
											const materialCutLength = form.getFieldValue(
												'material_cut_length'
											);
											const sawKerf = form.getFieldValue('saw_kerf');
											const materialCutQuantity = form.getFieldValue(
												'material_cut_quantity'
											);
											calculateRequiredMaterialLength(
												materialCutLength,
												sawKerf,
												materialCutQuantity
											);
										}
									}}
								>
									Reserve All Available
								</Checkbox>
							</Form.Item>
						</Col>
					) : null}
					<Col span={12}>
						<Form.Item
						label={`Length to Reserve (${calculateReservableLength(
							materialQuery?.data,
							materialLengthQuery?.data,
							props.selectedMaterialReservation,
							props.reserveMultipleLengths,
							props.lockedBuildingId
						)} IN. Available)`}
						name={'reserved_length'}
							rules={[
								{
									required: true,
								},
								{
									validator: (_, value) => {
										const reservableLength = calculateReservableLength(
											materialQuery?.data,
											materialLengthQuery?.data,
											props.selectedMaterialReservation,
											props.reserveMultipleLengths,
											props.lockedBuildingId
										);

										if (value > reservableLength) {
											return Promise.reject(
												new Error(
													`Cannot be greater than ${reservableLength} inches`
												)
											);
										}
										return Promise.resolve();
									},
								},
							]}
						>
							<InputNumber
								min={1}
								style={{ width: '100%', marginBottom: 0 }}
								suffix={'inches'}
								controls={false}
								readOnly={true}
							/>
						</Form.Item>
					</Col>
				</Row>
			</Form>
		</Modal>
	);
};

export default MaterialReservationModal;
